import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaBoxes, FaCalendarAlt } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import SaveButton from '~/components/Buttons/Save';
import Toggle from '~/components/Toggle';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Wrapper } from './styles';

interface IFormEvent {
  name: string;
  active: boolean;
}

interface ILocation {
  id: string;
  name: string;
  active: boolean;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
});

const New: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, control, errors } = useForm<IFormEvent>({
    resolver: yupResolver(schema),
    defaultValues: {
      active: state?.active,
    },
  });

  const submitForm = useCallback(
    async (data: IFormEvent) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/items/events', data);

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Evento cadastrado com sucesso.',
          });
        } else {
          await api.put(`/items/events`, { id: state.id, ...data });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Evento atualizado com sucesso.',
          });
        }

        history.push('/items/events');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  const onExit = () => {
    history.push('/items/events');
  };

  return (
    <Container>
      <BreadCrumb
        main="Itens"
        path="/items/main"
        firstChildPath="/items/events"
        firstChild="Eventos"
        secondChild={state === undefined ? 'Novo Evento' : 'Edição de Evento'}
        mainIcon={FaBoxes}
        firstChildIcon={FaCalendarAlt}
        secondChildIcon={FaCalendarAlt}
        mainColored
        firstChildColored
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <div
          style={{ maxWidth: 700, display: 'flex', flexWrap: 'wrap', gap: 10 }}
        >
          <Wrapper style={{ width: 350 }}>
            <DefaultInput
              name="name"
              labelFor="name"
              labelText="Nome"
              type="text"
              defaultValue={state?.name}
              error={errors?.name?.message}
              ref={register}
              maxLength={60}
            />
          </Wrapper>

          <Wrapper style={{ width: 100 }}>
            <Controller
              render={props => (
                <Toggle
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="active"
              defaultValue
              control={control}
            />
          </Wrapper>
        </div>

        <div
          style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end' }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma gravação do evento?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default New;
